import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CardGiftcard as GiftIcon,
  Image as ImageIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import api from '../services/api';

const GIVEAWAY_TYPES = [
  { value: 'physical', label: '📦 Physical Items', description: 'Electronics, gadgets, merchandise, etc.' },
  { value: 'services', label: '🛎️ Services', description: 'Subscriptions, memberships, consultations, etc.' },
  { value: 'cash', label: '💵 Cash Prize', description: 'Money, gift cards, vouchers' },
  { value: 'holiday', label: '✈️ Holiday/Travel', description: 'Trips, vacations, travel packages' },
  { value: 'car', label: '🚗 Vehicle', description: 'Cars, motorcycles, bikes' },
  { value: 'experience', label: '🎭 Experience', description: 'Events, concerts, meet & greets' },
  { value: 'digital', label: '💻 Digital', description: 'Software, games, NFTs, crypto' },
  { value: 'other', label: '🎁 Other', description: 'Custom prizes' }
];

const GIVEAWAY_STATUS = [
  { value: 'draft', label: 'Draft', color: 'default' },
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'paused', label: 'Paused', color: 'warning' },
  { value: 'ended', label: 'Ended', color: 'error' }
];

const GIVEAWAY_TIERS = [
  {
    value: 'main',
    label: 'Main Giveaway (Paid Users Only)',
    description: 'High-value giveaways available only to Essential, Professional, and Premium members',
    color: '#FFD700',
    icon: '👑'
  },
  {
    value: 'normal',
    label: 'Normal Giveaway (All Users)',
    description: 'Low-tier giveaways available to all users including free members',
    color: '#4CAF50',
    icon: '🎁'
  }
];

interface Giveaway {
  id: string;
  name: string;
  description: string;
  type: string;
  tier?: string; // 'main' or 'normal'
  status: string;
  start_date: string;
  end_date: string;
  image_url?: string;
  prize_details?: string;
  entry_requirements?: string;
  max_participants?: number;
  current_participants?: number;
  metadata?: any;
  rules?: any;
}

const GiveawaysEnhanced: React.FC = () => {
  const [giveaways, setGiveaways] = useState<Giveaway[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedGiveaway, setSelectedGiveaway] = useState<Giveaway | null>(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'physical',
    tier: 'normal',
    status: 'draft',
    start_date: '',
    end_date: '',
    image_url: '',
    prize_details: '',
    entry_requirements: '',
    max_participants: 0,
    livestream_link: ''
  });

  useEffect(() => {
    fetchGiveaways();
  }, []);

  const fetchGiveaways = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/giveaways');
      const data = response.data;
      setGiveaways(Array.isArray(data) ? data : []);
      setError('');
    } catch (err: any) {
      console.error('Error fetching giveaways:', err);
      setError('Failed to load giveaways');
      setGiveaways([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (giveaway?: Giveaway) => {
    if (giveaway) {
      setEditMode(true);
      setSelectedGiveaway(giveaway);

      // Parse metadata if it exists
      const metadata = giveaway.metadata ?
        (typeof giveaway.metadata === 'string' ? JSON.parse(giveaway.metadata) : giveaway.metadata)
        : {};

      // Parse rules if it exists
      const rules = giveaway.rules ?
        (typeof giveaway.rules === 'string' ? JSON.parse(giveaway.rules) : giveaway.rules)
        : {};

      setFormData({
        name: giveaway.name || '',
        description: giveaway.description || '',
        type: rules.type || giveaway.type || 'physical',
        tier: metadata.tier || giveaway.tier || 'normal',
        status: giveaway.status || 'draft',
        start_date: giveaway.start_date ? giveaway.start_date.split('T')[0] : '',
        end_date: giveaway.end_date ? giveaway.end_date.split('T')[0] : '',
        image_url: metadata.image_url || giveaway.image_url || '',
        prize_details: metadata.prize_details || giveaway.prize_details || '',
        entry_requirements: metadata.entry_requirements || giveaway.entry_requirements || '',
        max_participants: giveaway.max_participants || 0,
        livestream_link: metadata.livestream_link || ''
      });
    } else {
      setEditMode(false);
      setSelectedGiveaway(null);
      setFormData({
        name: '',
        description: '',
        type: 'physical',
        tier: 'normal',
        status: 'draft',
        start_date: '',
        end_date: '',
        image_url: '',
        prize_details: '',
        entry_requirements: '',
        max_participants: 0,
        livestream_link: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGiveaway(null);
    setEditMode(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await api.post('/admin/upload-giveaway', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Backend returns 'url' field
      if (response.data.url) {
        handleInputChange('image_url', response.data.url);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Prepare metadata
      const metadata = {
        prize_details: formData.prize_details,
        entry_requirements: formData.entry_requirements,
        image_url: formData.image_url,
        tier: formData.tier,
        livestream_link: formData.livestream_link
      };

      const apiData = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : new Date().toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : new Date().toISOString(),
        max_participants: parseInt(formData.max_participants.toString()) || 0,
        metadata: JSON.stringify(metadata),
        rules: JSON.stringify({ type: formData.type })
      };

      if (editMode && selectedGiveaway) {
        await api.put(`/admin/giveaways/${selectedGiveaway.id}`, apiData);
      } else {
        await api.post('/admin/giveaways', apiData);
      }

      handleCloseDialog();
      fetchGiveaways();
    } catch (err: any) {
      console.error('Error saving giveaway:', err);
      setError(err.message || 'Failed to save giveaway');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this giveaway?')) return;

    try {
      await api.delete(`/admin/giveaways/${id}`);
      fetchGiveaways();
    } catch (err) {
      console.error('Error deleting giveaway:', err);
      setError('Failed to delete giveaway');
    }
  };

  const getStatusChip = (status: string) => {
    const statusObj = GIVEAWAY_STATUS.find(s => s.value === status);
    return (
      <Chip
        label={statusObj?.label || status}
        color={statusObj?.color as any || 'default'}
        size="small"
      />
    );
  };

  if (loading && giveaways.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Giveaways Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Giveaway
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {giveaways.map((giveaway) => {
          const metadata = giveaway.metadata ?
            (typeof giveaway.metadata === 'string' ? JSON.parse(giveaway.metadata) : giveaway.metadata)
            : {};
          const imageUrl = metadata.image_url || giveaway.image_url;
          const tier = metadata.tier || giveaway.tier || 'normal';
          const tierInfo = GIVEAWAY_TIERS.find(t => t.value === tier);

          return (
            <Grid item xs={12} sm={6} md={4} key={giveaway.id}>
              <Card sx={{ position: 'relative' }}>
                {/* Tier Badge */}
                {tier === 'main' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: tierInfo?.color || '#FFD700',
                      color: '#000',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      zIndex: 1,
                      boxShadow: 2
                    }}
                  >
                    {tierInfo?.icon} MAIN
                  </Box>
                )}
                {imageUrl && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={imageUrl.startsWith('http') ? imageUrl : `http://localhost:5002${imageUrl}`}
                    alt={giveaway.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                {!imageUrl && (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200'
                    }}
                  >
                    <GiftIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                  </Box>
                )}
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {giveaway.name}
                    </Typography>
                    {getStatusChip(giveaway.status)}
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={tierInfo?.label || 'Normal Giveaway'}
                      size="small"
                      sx={{
                        bgcolor: tier === 'main' ? '#FFF3CD' : '#E8F5E9',
                        color: tier === 'main' ? '#856404' : '#2E7D32',
                        fontWeight: 500
                      }}
                    />
                    {(() => {
                      const rules = giveaway.rules ?
                        (typeof giveaway.rules === 'string' ? JSON.parse(giveaway.rules) : giveaway.rules)
                        : {};
                      const prizeType = GIVEAWAY_TYPES.find(t => t.value === (rules.type || giveaway.type));
                      return prizeType ? (
                        <Chip
                          label={prizeType.label}
                          size="small"
                          sx={{
                            bgcolor: '#E3F2FD',
                            color: '#1976D2',
                            fontWeight: 500
                          }}
                        />
                      ) : null;
                    })()}
                    {metadata.livestream_link && (
                      <Chip
                        label="🔴 LIVE"
                        size="small"
                        sx={{
                          bgcolor: '#FFEBEE',
                          color: '#C62828',
                          fontWeight: 'bold',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {giveaway.description}
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      Start: {giveaway.start_date ? new Date(giveaway.start_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      End: {giveaway.end_date ? new Date(giveaway.end_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                    {(giveaway.max_participants && giveaway.max_participants > 0) && (
                      <Typography variant="caption" color="text.secondary">
                        Participants: {giveaway.current_participants || 0} / {giveaway.max_participants}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(giveaway)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(giveaway.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {giveaways.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <GiftIcon sx={{ fontSize: 100, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No giveaways yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first giveaway to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Giveaway
          </Button>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Giveaway' : 'Create New Giveaway'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Giveaway Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />

            <FormControl fullWidth required>
              <InputLabel>Giveaway Tier</InputLabel>
              <Select
                value={formData.tier}
                label="Giveaway Tier"
                onChange={(e) => handleInputChange('tier', e.target.value)}
              >
                {GIVEAWAY_TIERS.map(tier => (
                  <MenuItem key={tier.value} value={tier.value}>
                    <Box>
                      <Typography variant="body1">
                        {tier.icon} {tier.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {tier.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {formData.tier === 'main'
                  ? '👑 Main giveaways are high-value and only visible to paid members (Essential, Professional, Premium)'
                  : '🎁 Normal giveaways are low-tier and visible to all users including free members'}
              </Typography>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Prize Type</InputLabel>
              <Select
                value={formData.type}
                label="Prize Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                {GIVEAWAY_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1">
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {GIVEAWAY_STATUS.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  required
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  required
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Max Participants (0 for unlimited)"
              type="number"
              fullWidth
              value={formData.max_participants}
              onChange={(e) => handleInputChange('max_participants', e.target.value)}
            />

            <TextField
              label="Prize Details"
              fullWidth
              multiline
              rows={2}
              value={formData.prize_details}
              onChange={(e) => handleInputChange('prize_details', e.target.value)}
              placeholder="Describe what winners will receive"
            />

            <TextField
              label="Entry Requirements"
              fullWidth
              multiline
              rows={2}
              value={formData.entry_requirements}
              onChange={(e) => handleInputChange('entry_requirements', e.target.value)}
              placeholder="What do users need to do to enter?"
            />

            <TextField
              label="Livestream Link (Optional)"
              fullWidth
              value={formData.livestream_link}
              onChange={(e) => handleInputChange('livestream_link', e.target.value)}
              placeholder="https://youtube.com/watch?v=... or https://twitch.tv/..."
              helperText="Add a YouTube, Twitch, or other livestream link for live giveaway events"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Giveaway Image
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploading ? <CircularProgress size={20} /> : <ImageIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {formData.image_url && (
                  <Typography variant="caption" color="text.secondary">
                    Image uploaded
                  </Typography>
                )}
              </Stack>
              {formData.image_url && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={formData.image_url.startsWith('http') ? formData.image_url : `http://localhost:5002${formData.image_url}`}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.description || !formData.start_date || !formData.end_date}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GiveawaysEnhanced;
